import { Box, BoxProps, useToast } from '@chakra-ui/react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
import { useAppointment } from '../../../../../../../../../../../../hooks/appointment';
import { useAuth } from '../../../../../../../../../../../../hooks/auth';
import { listAppointmentProductsService } from '../../../../../../../../../../../../services/Appointments/ListAppointmentProductsService';
import { translateError } from '../../../../../../../../../../../../utils/errors';
import {
  maskDateTime,
  maskMoney,
  maskShortDate,
} from '../../../../../../../../../../../../utils/formatters/handleMask';
import {
  AppointmentProductTable,
  IAppointmentProductTableItem,
} from '../../../../../../../components/AppointmentProductTable';

interface ILoadProductsProps {
  name?: string;
  page?: number;
}

export const AppointmentProductList = (props: BoxProps): JSX.Element => {
  const toast = useToast();

  const { venture: authenticatedVenture } = useAuth();

  const { appointment } = useAppointment();

  const [appointmentProductList, setAppointmentProductList] = useState<
    IAppointmentProductTableItem[]
  >([]);
  const [searchParam, setSearchParam] = useState<string>();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>();

  const loadProducts = useCallback(
    async ({ name, page }: ILoadProductsProps) => {
      if (!appointment?.id) {
        return;
      }

      try {
        const { items: appointmentProducts, pages } =
          await listAppointmentProductsService({
            name,
            page,
            sort: 'createdAt',
            order: 'DESC',
            appointmentId: appointment.id,
          });

        const appointmentProductTableItem =
          appointmentProducts.map<IAppointmentProductTableItem>(
            (appointmentProduct) => ({
              ...appointmentProduct,
              formattedCreatedAt: maskDateTime(appointmentProduct.createdAt),
              formattedPrice: maskMoney(appointmentProduct.price),
              formattedStart: appointmentProduct.startAt
                ? maskShortDate(appointmentProduct.startAt)
                : '-',
              formattedFinish: appointmentProduct.finishAt
                ? maskShortDate(appointmentProduct.finishAt)
                : '-',
            }),
          );

        setTotalPages(pages);
        setAppointmentProductList(appointmentProductTableItem);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados dos produtos, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [appointment?.id, toast],
  );

  useEffect(() => {
    setTotalPages(1);
    setAppointmentProductList([]);

    loadProducts({
      name: searchParam,
      page: currentPage,
    });
  }, [currentPage, loadProducts, authenticatedVenture, searchParam]);

  const handleSearch = useCallback((value: string) => {
    setSearchParam(value);
  }, []);

  const debounceSearch = debounce(handleSearch, 1000);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <Box {...props}>
      <AppointmentProductTable
        appointmentProducts={appointmentProductList}
        currentPage={currentPage}
        onSearch={debounceSearch}
        onPageChange={handlePageChange}
        totalPages={totalPages}
      />
    </Box>
  );
};
